import { SettingsV2 } from '@dixa/messenger-api-payloads';

import { Secret } from './messengers.types';

export type GenerateSecretRequest = {
  algorithm: 'A256CBC-HS512';
};

export type GenerateSecretResponse = {
  // base64 url encoded string
  secret: string;
};

export type DecryptPayloadRequest = {
  payload: string;
};

export type DecryptPayloadResponse = {
  secretId: string;
  decrypted: string;
};

type TokenField = 'jti' | 'iat' | 'email' | 'name';

export type DecryptPayloadInvalidTokenErrorResponse = {
  type: 'parse_error' | 'no_matching_secrets';
};

export type DecryptPayloadMissingFieldsErrorResponse = {
  type: 'missing_fields';
  decrypted: string;
  missing_fields: TokenField[];
};

export type DecryptPayloadErrorResponse =
  | DecryptPayloadInvalidTokenErrorResponse
  | DecryptPayloadMissingFieldsErrorResponse;

export const DecryptPayloadErrorResponse = {
  is: (error: unknown): error is DecryptPayloadErrorResponse => {
    if (typeof error === 'object' && error !== null && 'type' in error) {
      const { type } = error;

      if (type === 'parse_error' || type === 'no_matching_secrets') {
        return true;
      }

      if (
        type === 'missing_fields' &&
        'decrypted' in error &&
        'missing_fields' in error
      ) {
        const { decrypted, missing_fields } = error;
        if (typeof decrypted === 'string' && Array.isArray(missing_fields)) {
          return true;
        }
      }
    }

    return false;
  },
  mkErrorMessage: (
    error: DecryptPayloadErrorResponse,
  ): { decrypted?: string; message: string } => {
    switch (error.type) {
      case 'parse_error': {
        return { message: 'Parse error.' };
      }
      case 'no_matching_secrets': {
        return { message: 'No matching secrets.' };
      }
      case 'missing_fields': {
        const missingFields = error.missing_fields.join(', ');
        return {
          message: `Missing fields: ${missingFields}.`,
          decrypted: error.decrypted,
        };
      }
    }
  },
};

export type SecretInformation = {
  id: string;
  encryption_type: 'jwe_symmetric';
  created_at: string;
  description?: string;
};

export type CreateSecretRequest = {
  encryption_type: 'jwe_symmetric';
  secret: string;
  description?: string;
};

export type PatchSecretRequest = {
  description?: string;
};

export type GenerateSecretApiResponse = GenerateSecretResponse;
export type GenerateSecretApiArg = void;

export type ReadSecretsApiResponse = Secret[];
export type ReadSecretsApiArg = {
  messengerId: string;
};

export type CreateSecretApiResponse = Secret;
export type CreateSecretApiArg = {
  messengerId: string;
  secret: string;
  description?: string;
};

export type UpdateSecretApiResponse = Secret;
export type UpdateSecretApiArg = {
  messengerId: string;
  secretId: string;
  description?: string;
};

export type DeleteSecretApiResponse = void;
export type DeleteSecretApiArg = {
  messengerId: string;
  secretId: string;
};

export type DecryptTokenApiResponse = DecryptPayloadResponse;
export type DecryptTokenApiArg = {
  messengerId: string;
  payload: string;
};

export type VerifyElevioAccountResponse = void;
export type VerifyElevioAccountApiArg = {
  accountId: string;
};

export type ReadGlobalFallbackSettingsResponse = SettingsV2.Settings;
export type ReadGlobalFallbackSettingsApiArg = void;
